import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  useNavigate,
  useParams,
  useSearchParams,
  createSearchParams,
  useMatch,
} from 'react-router-dom';
import { api } from '../../api';
import { declineRetailerRequest } from './actions';
import { createGroup } from './actions';
import { deleteRetailer } from './actions';
import {
  customerSupportDataSelector,
  selectClientOptionsSelector,
} from 'redux/selectors';
import SessionStorageManager from 'utils/session-storage-manager';
import _debounce from 'lodash/debounce';
import { useLocation } from 'react-router-dom';

/**
 *
 * useCustomers() is Custom Hook where all logic is written for My-Customers Page
 *
 */
export default function useCustomers() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const supportData = useSelector(customerSupportDataSelector);

  const { selectedTab } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  /** state to store selected value for deciding which page to render. Retailers,Buyers,Groups  */
  const [selected, setSelected] = useState(
    (() => {
      if (selectedTab) return selectedTab;
      else return 'retailers';
    })()
  );
  const location = useLocation();
  useEffect(() => {
    selectedTab ? setSelected(selectedTab) : setSelected('retailers');
  }, [selectedTab]);
  /** state to store retailers  */
  const [retailers, setRetailers] = useState([]);

  /** state to store showAddNewRetailer for Add New Retailer Modal  */
  const [showAddRetailerForm, setShowAddRetailerForm] = useState(false);
  /** state to store showSendInviteEmail for Send Invite Email Modal  */
  const [showSendInviteEmail, setShowSendInviteEmail] = useState(false);
  /** state to store showCreateCustomerGroupModal for Add New Group Modal  */
  const [showCreateCustomerGroupModal, seShowCreateCustomerGroupModal] =
    useState(false);

  /** state to store global search field value  */
  const [retailerSearchValue, setRetailerSearchValue] = useState(
    searchParams.get('srch') || ''
  );

  /** state to store retailer search by alphabet value */
  const [alphabetValue, setAlphabetValue] = useState('');

  /** state to store page number */
  const [retailerPage, setRetailerPage] = useState(
    searchParams.get('page') || 1
  );
  /** state to store countryOptions for all dropDowns*/
  const [countryOptions, setCountryOptions] = useState([]);

  /** state to store isLoading */
  const [isLoading, setIsLoading] = useState(true);

  /** update url on initial page load */
  const isMyCustomer = useMatch('/my-customer');
  const isMyCustomerRetailer = useMatch('/my-customer/retailers');
  useEffect(() => {
    if (isMyCustomer) {
      navigate(`/my-customer/retailers?page=&srch=&filters=`, {
        replace: true,
      });
    }

    if (
      isMyCustomerRetailer &&
      !searchParams.get('srch') &&
      !searchParams.get('page') &&
      !searchParams.get('filters')
    ) {
      navigate(`/my-customer/retailers?page=&srch=&filters=`, {
        replace: true,
      });
    }
  }, [isMyCustomer, isMyCustomerRetailer]);

  useEffect(() => {
    setRetailerSearchValue('');
    setAlphabetValue('');
  }, [selected]);

  const clientOptions = useSelector(selectClientOptionsSelector);
  /** state to show payments */
  const [showPayments, setShowPayments] = useState(false);

  useEffect(() => {
    if (clientOptions?.features?.length > 0) {
      let features = clientOptions?.features;
      setShowPayments(!features.includes('no_cc_processor'));
    }
  }, [clientOptions]);

  const getRetailerDebounce = useCallback(_debounce(getRetailers, 1000), []);

  /** get retailers on page load/ on search/ on page change/ on filters change */
  useEffect(() => {
    let currentPage = location?.pathname?.split('/');
    if (currentPage?.some((x) => x === 'retailers')) {
      getRetailerDebounce({
        page: searchParams.get('page'), //page value from url params
        searchTerm: searchParams.get('srch'), //search value from url params
        filters: searchParams.get('filters') ?? '', //filters value from url params'
        alphabetValue: alphabetValue,
      });
    }
  }, [searchParams]);

  /** function to apply filters */
  function applyFilters(filtersString, filtersCount) {
    setSearchParams(
      createSearchParams({
        srch: retailerSearchValue,
        page: retailerPage,
        filters: filtersString ?? '',
        filtersCount: filtersCount ?? '',
      })
    );
  }

  /** apply filters on page change/ on search */
  useEffect(() => {
    const timer = setTimeout(() => {
      applyFilters(
        searchParams.get('filters'),
        searchParams.get('filtersCount')
      );
    }, 1000);

    return () => clearTimeout(timer);
  }, [retailerSearchValue, retailerPage]);

  /** reset retailer search value on change */
  useEffect(() => {
    setRetailerPage(1);
  }, [retailerSearchValue]);

  /** function to get list of retailers */
  async function getRetailers({ searchTerm, page, filters, alphabetValue }) {
    setIsLoading(true);
    let url = '';

    // if (alphabetValue) {
    //   // filters = searchParams.get('filters');
    //   // searchTerm = searchParams.get('search');
    // }

    searchTerm =
      searchTerm === undefined || searchTerm == null ? '' : searchTerm;
    filters = filters === undefined || searchTerm == null ? '' : filters;

    if (alphabetValue) {
      url = `/myRetailers?page=${page}&search=${searchTerm}&filters=${filters}&searchBy=${alphabetValue}`;
    } else {
      url = `/myRetailers?page=${page}&search=${searchTerm}&filters=${filters}`;
    }

    const res = await api.get(url);
    setRetailers(res.data);
    setIsLoading(false);
  }

  /** decline retailer request*/
  const confirmOnClickHandlerDeclineRetailer = async (payload) => {
    const response = await dispatch(declineRetailerRequest(payload));
    if (response) {
      getRetailers({
        searchTerm: searchParams.get('srch'),
        page: searchParams.get('page'),
        filters: searchParams.get('filters') ?? '',
        alphabetValue: alphabetValue,
      });
    }
  };
  /** create group*/
  const onCreateGroupSaveHandler = async (payload) => {
    const response = await dispatch(createGroup(payload));
    if (response) {
      SessionStorageManager.setSessionStorage('refetchGroup', true);
      navigate(`/my-customer/groups`);
    }
  };
  /** delete Retailer*/
  const confirmOnClickHandlerDeleteRetailer = async (payload) => {
    const response = await dispatch(deleteRetailer(payload));
    if (response) {
      getRetailers({
        searchTerm: searchParams.get('srch'),
        page: searchParams.get('page'),
        filters: searchParams.get('filters') ?? '',
        alphabetValue: alphabetValue,
      });
    }
  };
  /** fetch country options*/
  const getcountryOptions = async () => {
    /** set currency dropdown options*/
    supportData?.country?.forEach((country) => {
      setCountryOptions((prev) => {
        return [
          ...prev,
          {
            label: country.name,
            value: country.code,
            isDefault: false,
          },
        ];
      });
    });
  };

  useEffect(() => {
    getcountryOptions();
  }, []);

  return {
    selected,
    setSelected,
    retailers,
    showAddRetailerForm,
    setShowAddRetailerForm,
    showSendInviteEmail,
    setShowSendInviteEmail,
    showCreateCustomerGroupModal,
    seShowCreateCustomerGroupModal,
    setRetailerSearchValue,
    retailerSearchValue,
    retailerPage,
    setRetailerPage,
    applyFilters,
    confirmOnClickHandlerDeclineRetailer,
    onCreateGroupSaveHandler,
    confirmOnClickHandlerDeleteRetailer,
    countryOptions,
    setCountryOptions,
    navigate,
    showPayments,
    isLoading,
    alphabetValue,
    setAlphabetValue,
    getRetailers,
    searchParams,
  };
}
